/**
 * "You are viewing as …" — Step 99.
 *
 * The way OUT of impersonation, and the reason the roles API is exempt from
 * impersonation at all: if the control that ends it could itself be hidden by
 * the role being tested, a platform admin could browse into a low-privilege
 * role and have no way back but clearing cookies.
 *
 * Deliberately loud and deliberately fixed to the top. A quiet indicator is
 * how somebody spends twenty minutes wondering why a page 403s, and — worse —
 * how an impersonation gets left running. It expires on its own for the same
 * reason, and the countdown says so rather than lapsing mysteriously.
 */
import { useEffect, useState } from 'react';
import { Eye } from 'lucide-react';
import { apiSend } from '../../lib/api';
import { useAuth } from '../../lib/AuthContext';

function remaining(expiresAt: number): string {
  const ms = expiresAt - Date.now();
  if (ms <= 0) return 'expiring now';
  const mins = Math.ceil(ms / 60_000);
  return mins >= 60 ? '1 hour left' : `${mins} min left`;
}

export function ImpersonationBanner() {
  const { me } = useAuth();
  const [busy, setBusy] = useState(false);
  const active = me?.impersonating ?? null;
  // One tick a minute, only while the banner is up — the countdown is
  // information, not a reason to re-render the shell every second.
  const [, force] = useState(0);
  useEffect(() => {
    if (!active) return;
    const t = setInterval(() => force((n) => n + 1), 60_000);
    return () => clearInterval(t);
  }, [active]);

  if (!active) return null;

  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 bg-amber-500 px-4 py-1.5 text-sm text-black">
      <Eye className="h-4 w-4 shrink-0" />
      <span className="font-medium">
        Viewing as <span className="font-semibold">{active.roleKey}</span>
      </span>
      <span className="opacity-75">
        Your own access is unchanged, and everything you do is still recorded as you.
      </span>
      <span className="opacity-75">· {remaining(active.expiresAt)}</span>
      <div className="flex-1" />
      <button
        disabled={busy}
        onClick={async () => {
          setBusy(true);
          const res = await apiSend<{ roleId: string | null }>('/api/admin/roles/impersonate', 'DELETE');
          /**
           * Back to Roles and permissions with the role just tested already
           * open — digitaladmin's behaviour, at Kai's request. That is where
           * impersonation is started from, and where you want to be to adjust
           * a setting right after seeing what the role could and could not
           * reach.
           *
           * A full navigation rather than a client-side route change, also as
           * digitaladmin does: every cached query on this page was fetched
           * under the OTHER role's access, and a hard load discards all of it
           * at once rather than trusting each cache to notice.
           */
          const roleId = res.ok ? res.body?.roleId : null;
          window.location.assign(roleId ? `/admin/roles?role=${roleId}` : '/admin/roles');
        }}
        className="rounded border border-black/30 px-2 py-0.5 text-xs font-semibold hover:bg-black/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-black"
      >
        {busy ? 'Stopping…' : 'Stop'}
      </button>
    </div>
  );
}
